import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Brain,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  FileText,
  FlaskConical,
  Heart,
  Loader2,
  MessageCircle,
  Pill,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Patient } from '@/lib/types';
import {
  aiRecommendationService,
  IntellihealthMeta,
  parseRecommendation,
} from '@/services/aiRecommendationService';
import {
  ActionType,
  ClinicalAction,
  ExtractedActions,
  extractClinicalActions,
} from '@/lib/intellihealthActions';
import {
  isStructuredRecommendation,
  isLabRecommendation,
  isClinicalPatternRecommendation,
  StructuredRecommendation,
} from '@/types/intellihealthStructured';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { useIntelliHealthChat } from '@/contexts/IntelliHealthChatContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  prescriptionService,
  CreatePrescriptionPayload,
} from '@/services/prescriptionService';
import { createOrderFromIntelliHealth } from '@/services/labService';
import type { TherapyLabPanel } from '@/services/labService';
import PrescribeActionModal from '@/components/clinic/intellihealth/PrescribeActionModal';
import LabOrderModal from '@/components/clinic/intellihealth/LabOrderModal';
import type { LabOrderFormData } from '@/components/clinic/intellihealth/LabOrderModal';
import FollowupSchedulerModal from '@/components/clinic/intellihealth/FollowupSchedulerModal';
import TreatmentProtocolSelector from '@/components/clinic/intellihealth/TreatmentProtocolSelector';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IntelliHealthSectionProps {
  patient: Patient | null;
}

type SectionStatus = 'all-clear' | 'attention' | 'safety' | 'unknown';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const NIMCORE_PANEL_ID = 'a920ee66-372d-4dbe-958f-81e5208e09ac';
const DEFAULT_LAB_PANEL: TherapyLabPanel = {
  name: 'NovaMed Standard Panel',
  panelIds: [NIMCORE_PANEL_ID],
  price: 117.08,
  fastingRequired: true,
  markerCount: 89,
  collectionMethods: ['walk_in', 'at_home_phlebotomy'],
};

const ACTION_STYLES: Record<
  ActionType,
  { icon: React.ElementType; tone: string; label: string }
> = {
  prescribe: {
    icon: Pill,
    tone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    label: 'Prescribe',
  },
  protocol: {
    icon: ClipboardCheck,
    tone: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    label: 'Protocol',
  },
  labs: {
    icon: FlaskConical,
    tone: 'bg-blue-50 text-blue-700 border-blue-200',
    label: 'Order Labs',
  },
  followup: {
    icon: Calendar,
    tone: 'bg-purple-50 text-purple-700 border-purple-200',
    label: 'Schedule',
  },
  refer: {
    icon: UserPlus,
    tone: 'bg-amber-50 text-amber-700 border-amber-200',
    label: 'Refer',
  },
  monitor: {
    icon: Activity,
    tone: 'bg-teal-50 text-teal-700 border-teal-200',
    label: 'Monitor',
  },
  lifestyle: {
    icon: Heart,
    tone: 'bg-green-50 text-green-700 border-green-200',
    label: 'Lifestyle',
  },
  other: {
    icon: Sparkles,
    tone: 'bg-slate-50 text-slate-700 border-slate-200',
    label: 'View',
  },
};

const STATUS_META: Record<
  SectionStatus,
  { label: string; chipBg: string; chipText: string; ringTone: string; icon: React.ElementType }
> = {
  'all-clear': {
    label: 'All clear',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-800',
    ringTone: 'ring-emerald-200/60',
    icon: CheckCircle2,
  },
  attention: {
    label: 'Attention items',
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-800',
    ringTone: 'ring-amber-200/60',
    icon: AlertTriangle,
  },
  safety: {
    label: 'Safety review',
    chipBg: 'bg-red-100',
    chipText: 'text-red-800',
    ringTone: 'ring-red-200/60',
    icon: ShieldAlert,
  },
  unknown: {
    label: 'No recommendation yet',
    chipBg: 'bg-slate-100',
    chipText: 'text-slate-700',
    ringTone: 'ring-slate-200/60',
    icon: Sparkles,
  },
};

function extractKeyPoints(markdown: string): string[] {
  const stripped = markdown
    .replace(/#{1,6}\s+[^\n]+/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`[^`]+`/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*[-*+>]\s+/gm, '')
    .replace(/\n{2,}/g, ' ')
    .trim();
  return stripped
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 20)
    .slice(0, 3);
}

function relativeTime(iso?: string): string {
  if (!iso) return 'just now';
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return 'just now';
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function IntelliHealthSection({ patient }: IntelliHealthSectionProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { clinic } = useClinic();
  const queryClient = useQueryClient();
  const { setDrawerOpen, sendMessage } = useIntelliHealthChat();

  const [recommendation, setRecommendation] = useState<
    string | StructuredRecommendation | null
  >(null);
  const [meta, setMeta] = useState<IntellihealthMeta | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSoap, setShowSoap] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  const [prescribeOpen, setPrescribeOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ClinicalAction | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const runIdRef = useRef(0);
  const abortRef = useRef<(() => void) | null>(null);

  const patientForModals = useMemo(() => {
    if (!patient) return null;
    return {
      id: patient.id,
      first_name: patient.first_name,
      last_name: patient.last_name,
      full_name: patient.full_name,
      dob: patient.dob,
      gender: 0,
      gender_text: patient.gender || '',
      age: 0,
      status: patient.status || 'active',
      source_file: '',
      created_at: '',
      updated_at: '',
    } as const;
  }, [patient]);

  const fetchLatest = async () => {
    if (!patient?.id) {
      setIsLoading(false);
      return;
    }
    const runId = ++runIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const recs = await aiRecommendationService.getPatientRecommendations(
        patient.id,
        1,
      );
      if (runId !== runIdRef.current) return;
      setMeta(recs.data?.meta ?? null);
      const out = parseRecommendation(recs.data?.recommendation);
      if (typeof out === 'object' && out && isStructuredRecommendation(out)) {
        setRecommendation(out);
      } else if (typeof out === 'string') {
        setRecommendation(out);
      } else {
        setRecommendation(null);
      }
      setUpdatedAt(new Date().toISOString());
    } catch (err) {
      if (runId !== runIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      if (runId === runIdRef.current) setIsLoading(false);
    }
  };

  const refresh = () => {
    if (!patient?.id) return;
    const runId = ++runIdRef.current;
    abortRef.current?.();
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);
    setRecommendation('');
    let acc = '';
    const { abort } = aiRecommendationService.generateRecommendation(
      patient.id,
      undefined,
      (chunk) => {
        if (runId !== runIdRef.current) return;
        acc += chunk;
        setRecommendation(acc);
      },
      () => {
        if (runId !== runIdRef.current) return;
        setIsStreaming(false);
        setIsLoading(false);
        const parsed = parseRecommendation(acc);
        if (typeof parsed === 'object' && parsed && isStructuredRecommendation(parsed)) {
          setRecommendation(parsed);
        } else if (typeof parsed === 'string') {
          setRecommendation(parsed);
        }
        setUpdatedAt(new Date().toISOString());
      },
      (err) => {
        if (runId !== runIdRef.current) return;
        setIsStreaming(false);
        setIsLoading(false);
        setError(err.message);
      },
    );
    abortRef.current = abort;
  };

  useEffect(() => {
    fetchLatest();
    return () => abortRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  // Force Sync: re-homed from the deleted PatientIntelliHealthTab.
  // Pulls fresh patient data into IntelliHealth, then re-fetches this section
  // and invalidates related caches. Mirrors the right-panel header sync
  // (IntelliHealthRightPanel.tsx) and the inline chat sync (ChatMessage.tsx)
  // so all four surfaces call the same canonical endpoint.
  const handleForceSync = async () => {
    if (!patient?.id || isSyncing) return;
    setIsSyncing(true);
    try {
      await aiRecommendationService.syncPatientToIntellihealth(patient.id);
      toast({
        title: 'Patient data synced',
        description: 'IntelliHealth has fresh data for this patient.',
      });
      queryClient.invalidateQueries({
        queryKey: ['intellihealth-recommendations', patient.id],
      });
      // Re-fetch this section's latest recommendation after the sync lands.
      await fetchLatest();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      toast({
        title: 'Sync Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // ── Modal mutations ──

  const savePrescription = useMutation({
    mutationFn: (data: CreatePrescriptionPayload) =>
      prescriptionService.savePrescription(data),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Order Created',
          description: result.message || 'Order pending review',
          variant: 'success',
        });
        setPrescribeOpen(false);
        queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to create order',
          variant: 'destructive',
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const handlePrescribeSubmit = async (data: {
    medications: Array<{
      name?: string;
      dose?: string;
      sig?: string;
      quantity?: number;
      frequency?: string;
      rationale?: string;
      daysSupply?: number;
      refills?: number;
    }>;
    notes?: string;
  }) => {
    if (!patient?.id || !user?.practitionerId || !clinic?.id) {
      toast({
        title: 'Error',
        description: 'Missing patient, practitioner, or clinic context.',
        variant: 'destructive',
      });
      return;
    }
    const medicationRequests = data.medications.map((med) => ({
      medication: med.name || '',
      dose: med.dose || '',
      sig: med.sig || `Take ${med.dose || ''} ${med.frequency || 'as directed'}`.trim(),
      quantity: String(med.quantity || 30),
      unit: 'tablets',
      reason: med.rationale || selectedAction?.description || 'IntelliHealth recommendation',
      daysSupply: String(med.daysSupply || 30),
      refills: String(med.refills || 0),
      notes: data.notes || '',
      product_id: null,
      template_id: null,
    }));
    savePrescription.mutate({
      patientId: patient.id,
      practitionerId: user.practitionerId,
      clinicId: clinic.id,
      medicationRequests,
      priority: 1,
    });
  };

  const handleLabSubmit = async (data: LabOrderFormData) => {
    if (!patient?.id) return;
    try {
      await createOrderFromIntelliHealth({
        patientId: patient.id,
        panelId: data.panelId,
        collectionMethod: data.collectionMethod,
        patientDetails: {
          firstName: patient.first_name ?? '',
          lastName: patient.last_name ?? '',
          dob: patient.dob ?? '',
          gender: (patient.gender as 'male' | 'female' | 'unknown') ?? 'unknown',
          phone: patient.phone ?? '',
          email: patient.email ?? '',
        },
        patientAddress:
          data.patientAddress ?? { firstLine: '', city: '', state: '', zip: '' },
        fasting: data.fasting,
        notes: data.notes || undefined,
      });
      toast({
        title: 'Lab Order Created',
        description: 'Order placed via Quest Diagnostics.',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['labOrders'] });
      setLabOpen(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed',
        variant: 'destructive',
      });
    }
  };

  const handleProtocolSelect = (protocol: {
    name?: string;
    medications?: Array<{
      drug_name?: string;
      name?: string;
      strength?: string;
      dose?: string;
      sig?: string;
      quantity?: number;
      quantity_unit?: string;
      rationale?: string;
      days_supply?: number;
      refills?: number;
    }>;
  }) => {
    if (!patient?.id || !user?.practitionerId || !clinic?.id) return;
    const meds = protocol.medications || [];
    if (!meds.length) {
      toast({
        title: 'No Medications',
        description: 'Protocol has no medications.',
      });
      return;
    }
    savePrescription.mutate({
      patientId: patient.id,
      practitionerId: user.practitionerId,
      clinicId: clinic.id,
      medicationRequests: meds.map((m) => ({
        medication: m.drug_name || m.name || '',
        dose: m.strength || m.dose || '',
        sig: m.sig || '',
        quantity: String(m.quantity || 30),
        unit: m.quantity_unit || 'tablets',
        reason: m.rationale || `${protocol.name} protocol`,
        daysSupply: String(m.days_supply || 30),
        refills: String(m.refills || 0),
        notes: '',
        product_id: null,
        template_id: null,
      })),
      priority: 1,
    });
  };

  const dispatchAction = (action: ClinicalAction) => {
    setSelectedAction(action);
    if (action.type === 'prescribe') setPrescribeOpen(true);
    else if (action.type === 'labs') setLabOpen(true);
    else if (action.type === 'followup') setFollowupOpen(true);
    else if (action.type === 'protocol') setProtocolOpen(true);
    else
      toast({
        title: action.title,
        description: action.description,
      });
  };

  // ── Derive ──

  const markdown =
    typeof recommendation === 'string'
      ? recommendation
      : isLabRecommendation(recommendation as StructuredRecommendation)
      ? (recommendation as StructuredRecommendation & { summary?: string }).summary || ''
      : '';

  const summaryText = useMemo(() => {
    if (!recommendation) return '';
    if (typeof recommendation === 'string') return recommendation;
    if (isLabRecommendation(recommendation)) return recommendation.summary || '';
    if (isClinicalPatternRecommendation(recommendation))
      return recommendation.pattern_summary || '';
    return '';
  }, [recommendation]);

  const summaryPoints = useMemo(
    () => (summaryText ? extractKeyPoints(summaryText) : []),
    [summaryText],
  );

  const extracted: ExtractedActions = useMemo(() => {
    if (!recommendation || typeof recommendation !== 'string' || isStreaming) {
      return { actions: [], hasHighPriorityActions: false, summary: '' };
    }
    return extractClinicalActions(recommendation);
  }, [recommendation, isStreaming]);

  const structuredMeds = useMemo(() => {
    if (
      !recommendation ||
      typeof recommendation === 'string' ||
      !isLabRecommendation(recommendation)
    ) {
      return [];
    }
    return recommendation.medications || [];
  }, [recommendation]);

  const soapNote =
    recommendation &&
    typeof recommendation !== 'string' &&
    isLabRecommendation(recommendation)
      ? recommendation.soap
      : null;

  const status: SectionStatus = useMemo(() => {
    if (!recommendation) return 'unknown';
    const text = summaryText.toLowerCase();
    if (
      /critical|urgent|safety|hard.?stop|hematocrit.*5[4-9]|hy.?s law/.test(text)
    )
      return 'safety';
    if (
      extracted.hasHighPriorityActions ||
      /elevated|abnormal|trending up|out of range|deficien|insufficien|attention|review/.test(
        text,
      )
    )
      return 'attention';
    return 'all-clear';
  }, [recommendation, summaryText, extracted]);

  const statusMeta = STATUS_META[status];
  const StatusIcon = statusMeta.icon;

  const prescribeActions = extracted.actions.filter((a) => a.type === 'prescribe');
  const otherActions = extracted.actions.filter((a) => a.type !== 'prescribe');

  const askChat = (prompt: string) => {
    setDrawerOpen(true);
    setTimeout(
      () => sendMessage(prompt, patient?.id ?? undefined, 'patient-summary'),
      50,
    );
  };

  // ── Render ──

  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-inset transition-shadow hover:shadow-md',
        statusMeta.ringTone,
      )}
    >
      {/* Hero header — gradient using Nimbus brand palette
          (brand-teal → brand-soft-teal with a brand-copper accent glow). */}
      <div className='relative overflow-hidden bg-gradient-to-br from-[#4A8A9A] via-[#5EA8B8] to-[#4A8A9A]'>
        <div
          aria-hidden
          className='absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_top_left,rgba(255,255,255,0.45),transparent_60%),radial-gradient(circle_at_bottom_right,rgba(189,111,81,0.55),transparent_55%)]'
        />
        <div className='relative flex flex-wrap items-center justify-between gap-4 px-6 py-5'>
          <div className='flex items-center gap-4'>
            <div className='relative flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30 backdrop-blur'>
              <img
                src='/intellihealth-logo.png'
                alt='IntelliHealth'
                className='h-9 w-9 rounded-lg object-cover'
              />
              <span className='absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#BD6F51] text-[9px] font-bold text-white ring-2 ring-[#4A8A9A]'>
                AI
              </span>
            </div>
            <div>
              <h2 className='font-montserrat text-lg font-semibold text-white sm:text-xl'>
                IntelliHealth Clinical Summary
              </h2>
              <p className='text-xs text-white/80'>
                AI-powered decision support for {patient?.first_name || 'this patient'}
              </p>
            </div>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            {/* Force Sync — re-homed from the deleted PatientIntelliHealthTab.
                Calls aiRecommendationService.syncPatientToIntellihealth and
                re-fetches the section. Mirrors the right-panel header button. */}
            <Button
              variant='ghost'
              size='sm'
              onClick={handleForceSync}
              disabled={!patient?.id || isSyncing}
              className='h-7 rounded-full bg-white/15 px-3 text-[11px] font-semibold text-white hover:bg-white/25 hover:text-white'
            >
              {isSyncing ? (
                <Loader2 className='mr-1.5 h-3 w-3 animate-spin' />
              ) : (
                <RefreshCw className='mr-1.5 h-3 w-3' />
              )}
              {isSyncing ? 'Syncing…' : 'Force Sync'}
            </Button>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
                statusMeta.chipBg,
                statusMeta.chipText,
              )}
            >
              <StatusIcon className='h-3.5 w-3.5' />
              {statusMeta.label}
            </span>
            {meta?.status && meta.status !== 'ready' && (
              <span className='inline-flex items-center gap-1 rounded-full bg-[#BD6F51]/90 px-2.5 py-1 text-[11px] font-semibold text-white'>
                {meta.status}
              </span>
            )}
          </div>
        </div>

        <div className='relative flex items-center justify-between border-t border-white/15 px-6 py-2 text-[11px] text-white/80'>
          <span>
            {isStreaming
              ? 'Generating fresh recommendation…'
              : isLoading
              ? 'Loading recommendation…'
              : `Updated ${relativeTime(updatedAt ?? undefined)}`}
          </span>
          <Button
            variant='ghost'
            size='sm'
            onClick={refresh}
            disabled={isLoading || isStreaming}
            className='h-7 rounded-full bg-white/10 px-3 text-[11px] text-white hover:bg-white/20 hover:text-white'
          >
            {isLoading || isStreaming ? (
              <Loader2 className='mr-1.5 h-3 w-3 animate-spin' />
            ) : (
              <RefreshCw className='mr-1.5 h-3 w-3' />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className='p-6'>
        {isLoading && !recommendation ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : !recommendation ? (
          <EmptyState onGenerate={refresh} />
        ) : (
          <div className='space-y-5'>
            {/* Key takeaways */}
            {summaryPoints.length > 0 && (
              <div>
                <SectionHeader icon={Sparkles} title='Key takeaways' />
                <ul className='space-y-2'>
                  {summaryPoints.map((p, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-3 rounded-lg bg-gradient-to-r from-teal-50/70 to-transparent p-3'
                    >
                      <span className='mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white'>
                        {i + 1}
                      </span>
                      <p className='text-sm leading-relaxed text-slate-700'>{p}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summaryText && summaryPoints.length === 0 && (
              <div className='rounded-lg bg-gradient-to-br from-teal-50/70 to-white p-4'>
                <p className='text-sm leading-relaxed text-slate-700'>
                  {summaryText.length > 480
                    ? `${summaryText.slice(0, 480)}…`
                    : summaryText}
                </p>
              </div>
            )}

            {/* Medications */}
            {(prescribeActions.length > 0 || structuredMeds.length > 0) && (
              <div>
                <SectionHeader
                  icon={Pill}
                  title='Medication recommendations'
                  count={prescribeActions.length || structuredMeds.length}
                />
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  {structuredMeds.map((med, i) => (
                    <MedicationCard
                      key={`s-${i}`}
                      title={med.medication}
                      sig={med.sig}
                      badges={[med.dose, med.frequency].filter(Boolean) as string[]}
                      onPrescribe={() =>
                        dispatchAction({
                          id: `struct-med-${i}`,
                          type: 'prescribe',
                          title: `Prescribe ${med.medication}`,
                          description: med.sig || '',
                          priority: 'high',
                          sourceText: '',
                          metadata: {
                            medications: [
                              {
                                name: med.medication,
                                dose: med.dose,
                                frequency: med.frequency,
                                sig: med.sig,
                                quantity: med.quantity,
                                refills: med.refills,
                              },
                            ],
                          },
                        })
                      }
                    />
                  ))}
                  {prescribeActions.map((action) => {
                    const med = action.metadata.medications?.[0];
                    return (
                      <MedicationCard
                        key={action.id}
                        title={action.title.replace(/^Prescribe\s*/i, '')}
                        sig={action.description}
                        badges={[med?.dose, med?.frequency].filter(Boolean) as string[]}
                        onPrescribe={() => dispatchAction(action)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other clinical actions */}
            {otherActions.length > 0 && (
              <div>
                <SectionHeader icon={Sparkles} title='Clinical actions' />
                <div className='grid grid-cols-1 gap-2.5 sm:grid-cols-2'>
                  {otherActions.map((action) => {
                    const style = ACTION_STYLES[action.type] ?? ACTION_STYLES.other;
                    const Icon = style.icon;
                    return (
                      <button
                        key={action.id}
                        type='button'
                        onClick={() => dispatchAction(action)}
                        className='group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all hover:border-teal-300 hover:shadow-md'
                      >
                        <span
                          className={cn(
                            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border',
                            style.tone,
                          )}
                        >
                          <Icon className='h-4 w-4' />
                        </span>
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center justify-between gap-2'>
                            <span className='text-sm font-semibold text-slate-900'>
                              {action.title}
                            </span>
                            <span className='text-[10px] font-semibold uppercase tracking-wider text-teal-700 opacity-0 transition-opacity group-hover:opacity-100'>
                              {style.label}
                            </span>
                          </div>
                          <p className='mt-0.5 line-clamp-2 text-xs text-slate-500'>
                            {action.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SOAP note expander */}
            {soapNote && (
              <CollapsibleBlock
                icon={FileText}
                title='SOAP note'
                open={showSoap}
                onToggle={() => setShowSoap((v) => !v)}
              >
                <div className='space-y-3'>
                  {(['subjective', 'objective', 'assessment', 'plan'] as const).map(
                    (key) =>
                      soapNote[key] ? (
                        <div key={key}>
                          <div className='text-[10px] font-bold uppercase tracking-wider text-teal-700'>
                            {key}
                          </div>
                          <p className='mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700'>
                            {soapNote[key]}
                          </p>
                        </div>
                      ) : null,
                  )}
                </div>
              </CollapsibleBlock>
            )}

            {/* Reasoning expander (markdown form) */}
            {markdown && summaryPoints.length > 0 && markdown !== summaryText && (
              <CollapsibleBlock
                icon={Brain}
                title='Full reasoning'
                open={showReasoning}
                onToggle={() => setShowReasoning((v) => !v)}
              >
                <p className='whitespace-pre-wrap text-sm leading-relaxed text-slate-700'>
                  {markdown}
                </p>
              </CollapsibleBlock>
            )}

            {/* Data quality note */}
            {meta?.missing && meta.missing.length > 0 && (
              <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900'>
                <AlertTriangle className='mt-0.5 h-4 w-4 flex-shrink-0' />
                <div>
                  <div className='font-semibold'>Data quality</div>
                  <p className='mt-0.5'>
                    Missing inputs that may affect this recommendation:{' '}
                    {meta.missing.join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Footer actions */}
            <div className='flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4'>
              <div className='flex flex-wrap gap-2'>
                <button
                  type='button'
                  onClick={() => askChat('Summarize this patient')}
                  className='inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-white px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50'
                >
                  <MessageCircle className='h-3.5 w-3.5' /> Ask IntelliHealth
                </button>
                <button
                  type='button'
                  onClick={() => askChat('Review the latest labs for this patient')}
                  className='inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50'
                >
                  <FlaskConical className='h-3.5 w-3.5' /> Discuss labs
                </button>
                <button
                  type='button'
                  onClick={() => askChat('What changed since the last visit?')}
                  className='inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50'
                >
                  <Activity className='h-3.5 w-3.5' /> What changed?
                </button>
              </div>
              <span className='inline-flex items-center gap-1 text-[11px] text-slate-400'>
                <Sparkles className='h-3 w-3' /> Powered by IntelliHealth — review before acting
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Modals — same handlers as the original IntelliHealth tab */}
      {prescribeOpen && selectedAction && patientForModals && (
        <PrescribeActionModal
          open={prescribeOpen}
          onOpenChange={setPrescribeOpen}
          action={selectedAction}
          patient={patientForModals as never}
          onSubmit={handlePrescribeSubmit}
        />
      )}
      {labOpen && selectedAction && patientForModals && (
        <LabOrderModal
          open={labOpen}
          onOpenChange={setLabOpen}
          patient={patientForModals as never}
          therapyPanel={DEFAULT_LAB_PANEL}
          onSubmit={handleLabSubmit}
        />
      )}
      {followupOpen && selectedAction && patientForModals && (
        <FollowupSchedulerModal
          open={followupOpen}
          onOpenChange={setFollowupOpen}
          action={selectedAction}
          patient={patientForModals as never}
          onSubmit={async () => {
            toast({
              title: 'Follow-up Scheduled',
              description: `Scheduled for ${
                selectedAction.metadata.followupDays ?? 'N/A'
              } days from today`,
            });
            setFollowupOpen(false);
          }}
        />
      )}
      {protocolOpen && selectedAction && patientForModals && (
        <TreatmentProtocolSelector
          open={protocolOpen}
          onOpenChange={setProtocolOpen}
          action={selectedAction}
          patient={patientForModals as never}
          onSelectProtocol={handleProtocolSelect as never}
        />
      )}
    </section>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  count,
}: {
  icon: React.ElementType;
  title: string;
  count?: number;
}) {
  return (
    <div className='mb-3 flex items-center gap-2'>
      <Icon className='h-4 w-4 text-teal-600' />
      <h3 className='text-sm font-semibold text-slate-900'>{title}</h3>
      {count != null && count > 0 && (
        <Badge variant='outline' className='border-teal-200 text-[10px] text-teal-700'>
          {count}
        </Badge>
      )}
    </div>
  );
}

function MedicationCard({
  title,
  sig,
  badges,
  onPrescribe,
}: {
  title: string;
  sig?: string;
  badges: string[];
  onPrescribe: () => void;
}) {
  return (
    <div className='group relative flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:border-emerald-300 hover:shadow-md'>
      <div className='absolute -right-px -top-px h-12 w-12 rounded-bl-3xl rounded-tr-xl bg-gradient-to-br from-emerald-50 to-transparent opacity-0 transition-opacity group-hover:opacity-100' />
      <div className='flex items-start gap-2'>
        <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700'>
          <Pill className='h-4 w-4' />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-semibold text-slate-900'>{title}</div>
          {sig && <div className='mt-0.5 line-clamp-2 text-xs text-slate-500'>{sig}</div>}
        </div>
      </div>
      {badges.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {badges.map((b, i) => (
            <Badge
              key={i}
              variant='outline'
              className='border-slate-200 bg-slate-50 text-[10px] text-slate-700'
            >
              {b}
            </Badge>
          ))}
        </div>
      )}
      <Button
        size='sm'
        onClick={onPrescribe}
        className='mt-auto h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-700'
      >
        <Pill className='mr-1.5 h-3 w-3' /> Prescribe
      </Button>
    </div>
  );
}

function CollapsibleBlock({
  icon: Icon,
  title,
  open,
  onToggle,
  children,
}: {
  icon: React.ElementType;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className='rounded-xl border border-slate-200 bg-slate-50/40'>
      <button
        type='button'
        onClick={onToggle}
        className='flex w-full items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-left transition-colors hover:bg-slate-100/60'
        aria-expanded={open}
      >
        <span className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
          <Icon className='h-4 w-4 text-slate-500' />
          {title}
        </span>
        {open ? (
          <ChevronUp className='h-4 w-4 text-slate-500' />
        ) : (
          <ChevronDown className='h-4 w-4 text-slate-500' />
        )}
      </button>
      {open && <div className='border-t border-slate-200 bg-white px-4 py-3'>{children}</div>}
    </div>
  );
}

function LoadingState() {
  return (
    <div className='flex flex-col items-center justify-center gap-3 py-10 text-center'>
      <div className='relative'>
        <div className='absolute inset-0 animate-ping rounded-2xl bg-teal-200/50' />
        <div className='relative flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50'>
          <Sparkles className='h-7 w-7 text-teal-600' />
        </div>
      </div>
      <div>
        <p className='text-sm font-semibold text-slate-700'>Loading recommendation</p>
        <p className='text-xs text-slate-500'>Fetching the latest IntelliHealth output…</p>
      </div>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 py-10 text-center'>
      <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500'>
        <AlertTriangle className='h-7 w-7' />
      </div>
      <div>
        <p className='text-sm font-semibold text-slate-700'>Couldn't load recommendation</p>
        <p className='mt-0.5 max-w-sm text-xs text-red-500'>{error}</p>
      </div>
      <Button size='sm' onClick={onRetry}>
        <RefreshCw className='mr-1.5 h-3 w-3' /> Retry
      </Button>
    </div>
  );
}

function EmptyState({ onGenerate }: { onGenerate: () => void }) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 py-10 text-center'>
      <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-600'>
        <Sparkles className='h-7 w-7' />
      </div>
      <div>
        <p className='text-sm font-semibold text-slate-700'>No recommendation yet</p>
        <p className='max-w-sm text-xs text-slate-500'>
          Generate one when you have labs or other clinical signals to evaluate.
        </p>
      </div>
      <Button
        size='sm'
        onClick={onGenerate}
        className='bg-teal-600 text-white hover:bg-teal-700'
      >
        <Brain className='mr-1.5 h-3 w-3' /> Generate
      </Button>
    </div>
  );
}
